import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Tooltip,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  SelectChangeEvent,
  Checkbox,
  ListItemText,
  Avatar,
  Alert,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TableSortLabel from '@mui/material/TableSortLabel';
import Star from '@mui/icons-material/Star';
import StarBorder from '@mui/icons-material/StarBorder';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HelpIcon from '@mui/icons-material/Help';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import UndoIcon from '@mui/icons-material/Undo';
import { ProgramListItem } from '../../types';
import { ref as dbRef, get as dbGet } from 'firebase/database';
import { database, auth } from '../../firebase';
import { useAuthState } from '../../hooks/useAuthState';
import { useSchools } from '../../contexts/SchoolsContext';
import { usePrograms } from '../../contexts/ProgramsContext';
import { useUser } from '../../contexts/UserContext';
import CustomEligibilityIndicatorsModal from '../CustomEligibilityIndicatorsModal';

interface ProgramsListProps {
  programs: ProgramListItem[];
}

const ProgramsList: React.FC<ProgramsListProps> = ({ programs }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isAuthStateStable } = useAuthState();
  
  // Custom Eligibility Indicators modal state
  const [eligibilityIndicatorsModalOpen, setEligibilityIndicatorsModalOpen] = useState(false);
  
  // Helper function to get filter from URL or localStorage
  const getFilterFromURL = (key: string, defaultValue: any) => {
    const urlValue = searchParams.get(key);
    if (urlValue !== null) {
      if (key === 'province') {
        return urlValue.split(',').filter(Boolean);
      }
      return urlValue;
    }
    
    // Only fallback to localStorage if we're not in a category-specific view
    // This prevents old filters from persisting when switching categories
    const category = searchParams.get('category');
    if (!category) {
      const storedValue = localStorage.getItem(`programsList_${key}`);
      if (storedValue) {
        if (key === 'province') {
          return JSON.parse(storedValue);
        }
        return storedValue;
      }
    }
    return defaultValue;
  };

  // State for filters with URL persistence
  const [programSearch, setProgramSearch] = useState(() => getFilterFromURL('programSearch', ''));
  const [schoolSearch, setSchoolSearch] = useState(() => getFilterFromURL('schoolSearch', ''));
  const [provinceFilter, setProvinceFilter] = useState<string[]>(() => getFilterFromURL('province', []));
  const [eligibilityFilter, setEligibilityFilter] = useState<string>(() => getFilterFromURL('eligibility', 'all'));

  // Wrapper functions to update both state and URL/localStorage
  const updateProgramSearch = (value: string) => {
    setProgramSearch(value);
    const newSearchParams = new URLSearchParams(searchParams);
    if (value === '') {
      newSearchParams.delete('programSearch');
    } else {
      newSearchParams.set('programSearch', value);
    }
    setSearchParams(newSearchParams);
    
    // Only save to localStorage if not in a category-specific view
    const category = searchParams.get('category');
    if (!category) {
      localStorage.setItem('programsList_programSearch', value);
    }
  };

  const updateSchoolSearch = (value: string) => {
    setSchoolSearch(value);
    const newSearchParams = new URLSearchParams(searchParams);
    if (value === '') {
      newSearchParams.delete('schoolSearch');
    } else {
      newSearchParams.set('schoolSearch', value);
    }
    setSearchParams(newSearchParams);
    
    // Only save to localStorage if not in a category-specific view
    const category = searchParams.get('category');
    if (!category) {
      localStorage.setItem('programsList_schoolSearch', value);
    }
  };

  const updateProvinceFilter = (value: string[]) => {
    setProvinceFilter(value);
    const newSearchParams = new URLSearchParams(searchParams);
    if (value.length === 0) {
      newSearchParams.delete('province');
    } else {
      newSearchParams.set('province', value.join(','));
    }
    setSearchParams(newSearchParams);
    
    // Only save to localStorage if not in a category-specific view
    const category = searchParams.get('category');
    if (!category) {
      localStorage.setItem('programsList_province', JSON.stringify(value));
    }
  };

  const updateEligibilityFilter = (value: string) => {
    setEligibilityFilter(value);
    const newSearchParams = new URLSearchParams(searchParams);
    if (value === 'all') {
      newSearchParams.delete('eligibility');
    } else {
      newSearchParams.set('eligibility', value);
    }
    setSearchParams(newSearchParams);
    
    // Only save to localStorage if not in a category-specific view
    const category = searchParams.get('category');
    if (!category) {
      localStorage.setItem('programsList_eligibility', value);
    }
  };

  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({ key: 'school', direction: 'asc' });
  
  // State for managing favorites (read-only display)
  const [favoritePrograms, setFavoritePrograms] = useState<Set<string>>(new Set());
  

  
  // Get schools data for school images
  const { schools, loading: schoolsLoading } = useSchools();
  
  // Get calculation functions from ProgramsContext
  const { 
    calculateEligibilityForSchools, 
    isEligibilityCalculatedForSchools,
    getProgramEligibility,
    eligibilityError,
    eligibilityCalculationInProgress
  } = usePrograms();
  
  // Get user data for grade-specific icons
  const { user: currentUser } = useUser();
  
  // Helper function to get cached eligibility data from ProgramsContext
  const getCachedEligibility = (programId: string) => {
    return getProgramEligibility(programId);
  };
  
  // Helper function to get eligibility status for a program from the Programs Context
  const getEligibilityStatus = (programId: string) => {
    const eligibility = getCachedEligibility(programId);
    
    if (!eligibility) {
      return null;
    }
    
    // Debug logging for all programs to understand eligibility data
    console.log(`🔍 getEligibilityStatus Debug - Program ID: ${programId}`);
    console.log(`🔍 getEligibilityStatus Debug - ifEligible: ${eligibility.ifEligible}`);
    console.log(`🔍 getEligibilityStatus Debug - alertMessage: ${eligibility.alertMessage}`);
    console.log(`🔍 getEligibilityStatus Debug - calculatedAverage: ${eligibility.calculatedAverage}`);
    console.log(`🔍 getEligibilityStatus Debug - programAverage: ${eligibility.programAverage}`);
    console.log(`🔍 getEligibilityStatus Debug - missingCourses: ${eligibility.missingCourses?.length || 0}`);
    console.log(`🔍 getEligibilityStatus Debug - gradeIssues: ${eligibility.gradeIssues?.length || 0}`);
    console.log(`🔍 getEligibilityStatus Debug - nextYearSuggestion: ${eligibility.nextYearSuggestion}`);
    
    const userGrade = currentUser?.courses ? 
      (Object.values(currentUser.courses).some((course: any) => course.grade === '12' || course.grades === 12) ? '12' :
       Object.values(currentUser.courses).some((course: any) => course.grade === '11' || course.grades === 11) ? '11' : '10') : '10';
    console.log(`🔍 getEligibilityStatus Debug - userGrade: ${userGrade}`);
    
    if (userGrade === '12' && eligibility.calculatedAverage && eligibility.programAverage) {
      console.log(`🔍 getEligibilityStatus Debug - average check: ${eligibility.calculatedAverage < eligibility.programAverage}`);
    }
    
    // If ifEligible is explicitly false, return false
    if (eligibility.ifEligible === false) {
      return false;
    }
    
    // If ifEligible is explicitly true, return true
    if (eligibility.ifEligible === true) {
      return true;
    }
    
    // If ifEligible is null, it means there are some issues.
    // We need to determine if these issues make it "Not Yet Eligible" or "Other".
    if (eligibility.ifEligible === null) {
      const hasMissingCourses = (eligibility.missingCourses?.length || 0) > 0;
      const hasGradeIssues = (eligibility.gradeIssues?.length || 0) > 0;
      
      const userGrade = currentUser?.courses ? 
        (Object.values(currentUser.courses).some((course: any) => course.grade === '12' || course.grades === 12) ? '12' :
         Object.values(currentUser.courses).some((course: any) => course.grade === '11' || course.grades === 11) ? '11' : '10') : '10';
      
      const hasLowAverage = userGrade === '12' &&
                           eligibility.calculatedAverage !== undefined &&
                           eligibility.programAverage !== undefined &&
                           eligibility.calculatedAverage < eligibility.programAverage;

      // If there are any specific reasons for not being eligible (missing courses, grade issues, or low average for G12),
      // then it should be classified as "Not Yet Eligible" (false).
      if (hasMissingCourses || hasGradeIssues || hasLowAverage) {
        console.log(`🔍 getEligibilityStatus: Returning false for Not Yet Eligible (ifEligible: null, reasons: missingCourses=${hasMissingCourses}, gradeIssues=${hasGradeIssues}, lowAverage=${hasLowAverage}) - Program ID: ${programId}`);
        return false;
      }
    }
    
    // Default to null for other cases
    console.log(`🔍 getEligibilityStatus: Returning null for Program ID: ${programId} (ifEligible: ${eligibility.ifEligible}, no specific issues found)`);
    return null;
  };

  const handleSort = (columnKey: string) => {
    setSortConfig((prev) => {
      if (prev.key === columnKey) {
        return { key: columnKey, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key: columnKey, direction: 'asc' };
    });
  };

  const handleProgramClick = (programId: string) => {
    // Preserve current filter state in URL when navigating to program details
    const currentParams = new URLSearchParams(searchParams);
    const category = searchParams.get('category');
    
    // Ensure category parameter is preserved if it exists
    if (category) {
      currentParams.set('category', category);
    }
    
    navigate(`/programs/${programId}?${currentParams.toString()}`);
  };

  // Get unique provinces for the filter
  const provinces = useMemo(() => 
    Array.from(new Set(programs.map(program => program.province))).sort(),
    [programs]
  );

  const handleProvinceChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    const provinceArray = typeof value === 'string' ? value.split(',') : value;
    updateProvinceFilter(provinceArray);
  };

  // Load feed icons for all schools immediately when component mounts
  // Feed icons are now loaded upfront with schools, no need to load separately

  // Note: Eligibility data is now managed by ProgramsContext cache
  // No need to trigger calculations here - the cache will provide the data

  // Sync filter state with URL parameters and handle category changes
  useEffect(() => {
    const urlProgramSearch = searchParams.get('programSearch');
    const urlSchoolSearch = searchParams.get('schoolSearch');
    const urlProvince = searchParams.get('province');
    const urlEligibility = searchParams.get('eligibility');
    const urlCategory = searchParams.get('category');

    console.log('🔄 ProgramsList: URL params changed:', {
      urlProgramSearch,
      urlSchoolSearch,
      urlProvince,
      urlEligibility,
      urlCategory,
      currentProgramSearch: programSearch,
      currentSchoolSearch: schoolSearch,
      currentProvinceFilter: provinceFilter,
      currentEligibilityFilter: eligibilityFilter
    });

    // Handle category-specific filter clearing
    if (urlCategory) {
      // If we're in a category view and there are no explicit filter values in the URL,
      // clear any existing filters to start fresh
      if (!urlProgramSearch && !urlSchoolSearch && !urlProvince && !urlEligibility) {
        // Only clear if we have filters set but they're not in the URL
        if (programSearch || schoolSearch || provinceFilter.length > 0 || eligibilityFilter !== 'all') {
          console.log('🔄 ProgramsList: Category view detected, clearing filters');
          setProgramSearch('');
          setSchoolSearch('');
          setProvinceFilter([]);
          setEligibilityFilter('all');
          return; // Exit early to avoid setting filters below
        }
      }
    }

    // Sync filters from URL parameters
    if (urlProgramSearch !== null && urlProgramSearch !== programSearch) {
      setProgramSearch(urlProgramSearch);
    }
    if (urlSchoolSearch !== null && urlSchoolSearch !== schoolSearch) {
      setSchoolSearch(urlSchoolSearch);
    }
    if (urlProvince !== null) {
      const provinceArray = urlProvince.split(',').filter(Boolean);
      if (JSON.stringify(provinceArray) !== JSON.stringify(provinceFilter)) {
        setProvinceFilter(provinceArray);
      }
    }
    if (urlEligibility !== null && urlEligibility !== eligibilityFilter) {
      setEligibilityFilter(urlEligibility);
    }
  }, [searchParams]);



  const filteredPrograms = useMemo(() => {
    // Debug logging for all programs with eligibility data
    console.log(`🔍 ProgramsList Debug - Total programs: ${programs.length}`);
    console.log(`🔍 ProgramsList Debug - Current eligibilityFilter: ${eligibilityFilter}`);
    
    const programsWithEligibility = programs.filter(p => p.eligibility);
    console.log(`🔍 ProgramsList Debug - Programs with eligibility data: ${programsWithEligibility.length}`);
    
    // Log a few programs with eligibility data to see what we're working with
    programsWithEligibility.slice(0, 3).forEach(program => {
      const cachedEligibility = getCachedEligibility(program.id);
      console.log(`🔍 ProgramsList Debug - Sample program: ${program.program}`);
      console.log(`🔍 ProgramsList Debug - Sample ifEligible: ${cachedEligibility?.ifEligible}`);
      console.log(`🔍 ProgramsList Debug - Sample calculatedAverage: ${cachedEligibility?.calculatedAverage}`);
      console.log(`🔍 ProgramsList Debug - Sample programAverage: ${cachedEligibility?.programAverage}`);
    });
    
    return programs.filter((program) => {
      const programMatch = program.program.toLowerCase().includes(programSearch.toLowerCase());
      const schoolMatch = program.school.toLowerCase().includes(schoolSearch.toLowerCase());
      const provinceMatch = provinceFilter.length > 0 ? provinceFilter.includes(program.province) : true;
      
      // Eligibility filter
      let eligibilityMatch = true;
      if (eligibilityFilter !== 'all') {
        const eligibilityStatus = getEligibilityStatus(program.id);
        const hasEligibilityData = getCachedEligibility(program.id);
        
        // Debug logging for filter logic
        if (program.program && (program.program.includes('Computer Science') || program.program.includes('Engineering'))) {
          console.log(`🔍 Filter Debug - Program: ${program.program}`);
          console.log(`🔍 Filter Debug - eligibilityFilter: ${eligibilityFilter}`);
          console.log(`🔍 Filter Debug - eligibilityStatus: ${eligibilityStatus}`);
          console.log(`🔍 Filter Debug - hasEligibilityData: ${hasEligibilityData}`);
          console.log(`🔍 Filter Debug - cached eligibility.ifEligible: ${hasEligibilityData?.ifEligible}`);
        }
        
        if (eligibilityFilter === 'eligible') {
          eligibilityMatch = eligibilityStatus === true;
        } else if (eligibilityFilter === 'not-eligible') {
          eligibilityMatch = eligibilityStatus === false;
        } else if (eligibilityFilter === 'other') {
          eligibilityMatch = !hasEligibilityData || eligibilityStatus === null;
        }
        
        // Debug logging for filter results
        if (program.program && (program.program.includes('Computer Science') || program.program.includes('Engineering'))) {
          console.log(`🔍 Filter Result Debug - Program: ${program.program}`);
          console.log(`🔍 Filter Result Debug - eligibilityFilter: ${eligibilityFilter}`);
          console.log(`🔍 Filter Result Debug - eligibilityStatus: ${eligibilityStatus}`);
          console.log(`🔍 Filter Result Debug - eligibilityMatch: ${eligibilityMatch}`);
          console.log(`🔍 Filter Result Debug - will be included: ${eligibilityMatch}`);
        }
      }
      
      return programMatch && schoolMatch && provinceMatch && eligibilityMatch;
    });
  }, [programs, programSearch, schoolSearch, provinceFilter, eligibilityFilter]);

  const sortedPrograms = useMemo(() => {
    const arr = [...filteredPrograms];
    arr.sort((a, b) => {
      let aValue: any;
      let bValue: any;
      switch (sortConfig.key) {
        case 'program':
          aValue = a.program.toLowerCase();
          bValue = b.program.toLowerCase();
          break;
        case 'school':
          aValue = a.school.toLowerCase();
          bValue = b.school.toLowerCase();
          break;
        case 'programCode':
          aValue = a.programCode.toLowerCase();
          bValue = b.programCode.toLowerCase();
          break;
        case 'prerequisites':
          aValue = a.prerequisites.toLowerCase();
          bValue = b.prerequisites.toLowerCase();
          break;
        default:
          aValue = '';
          bValue = '';
      }
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filteredPrograms, sortConfig]);

  // Load user's favorite programs when auth state is stable and programs are loaded
  useEffect(() => {
    const loadFavorites = async () => {
      try {
        const currentUser = auth.currentUser;
        
        // Clear favorites if no user or auth state not stable
        if (!currentUser || !isAuthStateStable) {
          console.log('🔄 ProgramsList: Clearing favorites - no user or auth state not stable');
          setFavoritePrograms(new Set());
          return;
        }

        console.log('🔄 ProgramsList: Loading favorites for user:', currentUser.uid);

        const favoritesPromises = programs.map(async (program) => {
          const favoriteRef = dbRef(database, `favoriteSchoolProgram/${program.school}/${program.id}/${currentUser.uid}`);
          const snapshot = await dbGet(favoriteRef);
          return { programId: program.id, isFavorite: snapshot.exists() };
        });

        const results = await Promise.all(favoritesPromises);
        const favorites = new Set(
          results.filter(result => result.isFavorite).map(result => result.programId)
        );
        setFavoritePrograms(favorites);
        console.log('✅ ProgramsList: Loaded favorites:', Array.from(favorites));
      } catch (error) {
        console.error('Error loading favorites:', error);
        setFavoritePrograms(new Set());
      }
    };

    if (programs.length > 0) {
      loadFavorites();
    }
  }, [programs, user, isAuthStateStable]);



  return (
    <Box sx={{ width: '100%', px: 0.5 }}>
      {/* Search Bar */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
        <FormControl sx={{ minWidth: 180, maxWidth: 200 }}>
          <InputLabel>Eligibility</InputLabel>
          <Select
            value={eligibilityFilter}
            label="Eligibility"
            onChange={(e) => updateEligibilityFilter(e.target.value)}
          >
            <MenuItem value="all">Show All</MenuItem>
            <MenuItem value="eligible">Eligible</MenuItem>
            <MenuItem value="not-eligible">Not Yet Eligible</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </FormControl>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by program..."
          value={programSearch}
          onChange={(e) => updateProgramSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by school..."
          value={schoolSearch}
          onChange={(e) => updateSchoolSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <FormControl fullWidth>
          <InputLabel>Province</InputLabel>
          <Select
            multiple
            value={provinceFilter}
            label="Province"
            onChange={handleProvinceChange}
            renderValue={(selected) => {
              if (selected.length === 0) {
                return 'All Provinces';
              }
              if (selected.length === 1) {
                return selected[0];
              }
              return `${selected.length} provinces selected`;
            }}
          >
            {provinces.map((province) => (
              <MenuItem key={province} value={province}>
                <Checkbox checked={provinceFilter.includes(province)} />
                <ListItemText primary={province} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      
      {/* Eligibility Error Message */}
      {eligibilityError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {eligibilityError.includes('update your profile') ? (
            <Box>
              No course data found. Please{' '}
              <Typography
                component="span"
                variant="body2"
                color="primary"
                sx={{ 
                  cursor: 'pointer', 
                  textDecoration: 'underline',
                  fontWeight: 'inherit'
                }}
                onClick={() => navigate('/my-student-profile?tab=academics')}
              >
                update your profile
              </Typography>
              {' '}with courses and grades to view program eligibility.
            </Box>
          ) : (
            eligibilityError
          )}
        </Alert>
      )}
      
      {/* Eligibility Note */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          Estimated eligibility is customized for you based on the courses and grades entered in your{' '}
          <Typography
            component="span"
            variant="body2"
            color="primary"
            sx={{ 
              cursor: 'pointer', 
              textDecoration: 'underline',
              fontStyle: 'italic'
            }}
            onClick={() => navigate('/my-student-profile?tab=academics')}
          >
            profile
          </Typography>
          .{' '}
          Learn more about{' '}
          <Typography
            component="span"
            variant="body2"
            color="primary"
            sx={{ 
              cursor: 'pointer', 
              textDecoration: 'underline',
              fontStyle: 'italic'
            }}
            onClick={() => setEligibilityIndicatorsModalOpen(true)}
          >
            Custom Eligibility Indicators
          </Typography>
          .
        </Typography>
      </Box>
      
      {/* Results Count */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'space-between' }}>
        <Typography variant="subtitle1">
          {sortedPrograms.length} programs displayed
        </Typography>
      </Box>
      {/* Programs Display */}
      <Box sx={{ width: '100%', overflowX: 'auto' }}>
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
          <Box component="thead">
            <Box component="tr">
              <Box component="th" sx={{ p: 1, width: 50, textAlign: 'center', verticalAlign: 'bottom' }}>
                {/* No title for star column */}
              </Box>
              <Box component="th" sx={{ p: 1, width: 50, textAlign: 'center', verticalAlign: 'bottom' }}>
                {/* No title for eligibility column */}
              </Box>
              <Box component="th" sx={{ p: 1, minWidth: 225, maxWidth: 375, textAlign: 'left', verticalAlign: 'bottom', cursor: 'pointer' }} onClick={() => handleSort('program')}>
                <TableSortLabel
                  active={sortConfig.key === 'program'}
                  direction={sortConfig.key === 'program' ? sortConfig.direction : 'asc'}
                >
                  Program
                </TableSortLabel>
              </Box>
              <Box component="th" sx={{ p: 1, minWidth: 225, maxWidth: 375, textAlign: 'left', verticalAlign: 'bottom', cursor: 'pointer' }} onClick={() => handleSort('school')}>
                <TableSortLabel
                  active={sortConfig.key === 'school'}
                  direction={sortConfig.key === 'school' ? sortConfig.direction : 'asc'}
                >
                  School
                </TableSortLabel>
              </Box>
              <Box component="th" sx={{ p: 1, minWidth: 100, maxWidth: 120, textAlign: 'left', verticalAlign: 'bottom', cursor: 'pointer' }} onClick={() => handleSort('province')}>
                <TableSortLabel
                  active={sortConfig.key === 'province'}
                  direction={sortConfig.key === 'province' ? sortConfig.direction : 'asc'}
                >
                  Province
                </TableSortLabel>
              </Box>
            </Box>
          </Box>
          <Box component="tbody">
            {sortedPrograms.map((program) => (
              <Box
                component="tr"
                key={program.id}
                sx={{
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'grey.50' }
                }}
                onClick={() => handleProgramClick(program.id)}
              >
                <Box component="td" sx={{ p: 1, width: 50, textAlign: 'center', verticalAlign: 'top' }}>
                  <Box sx={{ 
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    color: favoritePrograms.has(program.id) ? '#8dd439' : 'grey.400',
                    p: 0.5
                  }}>
                    {favoritePrograms.has(program.id) ? (
                      <Star sx={{ fontSize: '20px' }} />
                    ) : (
                      <StarBorder sx={{ fontSize: '20px' }} />
                    )}
                  </Box>
                </Box>
                <Box component="td" sx={{ p: 1, width: 50, textAlign: 'center', verticalAlign: 'top' }}>
                  {/* Eligibility Indicator */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    {(() => {
                      const eligibilityStatus = getEligibilityStatus(program.id);
                      const hasEligibilityData = getProgramEligibility(program.id);
                      
                      // Get user's grade from courses
                      const userGrade = currentUser?.courses ? 
                        (Object.values(currentUser.courses).some((course: any) => course.grade === '12' || course.grades === 12) ? '12' :
                         Object.values(currentUser.courses).some((course: any) => course.grade === '11' || course.grades === 11) ? '11' : '10') : '10';
                      
                      const isGrade10 = userGrade === '10';
                      
                      console.log(`🎨 Icon Debug - Program: ${program.program}, eligibilityStatus: ${eligibilityStatus}, userGrade: ${userGrade}, isGrade10: ${isGrade10}`);
                      
                      if (!hasEligibilityData) {
                        // Show blinking blue icon if calculation is in progress
                        if (eligibilityCalculationInProgress) {
                          return (
                            <Tooltip title="Calculating eligibility..." arrow>
                              <Box sx={{
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                backgroundColor: '#1976d2', // Blue color
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                animation: 'pulse 1.5s ease-in-out infinite',
                                '@keyframes pulse': {
                                  '0%': {
                                    opacity: 1,
                                    transform: 'scale(1)',
                                  },
                                  '50%': {
                                    opacity: 0.7,
                                    transform: 'scale(1.1)',
                                  },
                                  '100%': {
                                    opacity: 1,
                                    transform: 'scale(1)',
                                  },
                                }
                              }}>
                                <HelpIcon sx={{ color: 'white', fontSize: 16 }} />
                              </Box>
                            </Tooltip>
                          );
                        } else {
                          return (
                            <Tooltip title="Eligibility not checked" arrow>
                              <HelpIcon sx={{ color: 'grey.400', fontSize: 20 }} />
                            </Tooltip>
                          );
                        }
                      } else if (eligibilityStatus === true) {
                        const programEligibility = programs.find(p => p.id === program.id)?.eligibility;
                        if (isGrade10) {
                          // Grade 10: Fosterway green circle with white full vertical arrow for on track
                          return (
                            <Tooltip 
                              title={
                                <div>
                                  <div>{programEligibility?.alertMessage || 'You are on the right path for this program!'}</div>
                                  {programEligibility?.nextYearSuggestion && programEligibility.nextYearSuggestion !== "" && (
                                    <div style={{ marginTop: '8px', fontStyle: 'italic', color: '#f44336' }}>
                                      💡 {programEligibility.nextYearSuggestion}
                                    </div>
                                  )}
                                </div>
                              }
                              arrow
                              componentsProps={{
                                tooltip: {
                                  sx: {
                                    backgroundColor: '#f5f5f5',
                                    color: '#333',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                    fontSize: '14px',
                                    padding: '12px',
                                    maxWidth: '300px'
                                  }
                                }
                              }}
                            >
                              <Box sx={{
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                backgroundColor: '#8dd439', // Fosterway green (lighter shade)
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <ArrowUpwardIcon sx={{ color: 'white', fontSize: 16 }} />
                              </Box>
                            </Tooltip>
                          );
                        } else {
                          // Other grades: Original green check
                          return (
                            <Tooltip 
                              title={
                                <div>
                                  <div>{programEligibility?.alertMessage || 'You are eligible for this program'}</div>
                                  {programEligibility?.nextYearSuggestion && programEligibility.nextYearSuggestion !== "" && (
                                    <div style={{ marginTop: '8px', fontStyle: 'italic', color: '#f44336' }}>
                                      💡 {programEligibility.nextYearSuggestion}
                                    </div>
                                  )}
                                </div>
                              }
                              arrow
                              componentsProps={{
                                tooltip: {
                                  sx: {
                                    backgroundColor: '#f5f5f5',
                                    color: '#333',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                    fontSize: '14px',
                                    padding: '12px',
                                    maxWidth: '300px'
                                  }
                                }
                              }}
                            >
                              <Box sx={{
                                width: 24,
                                height: 24,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <CheckCircleIcon sx={{ color: 'success.main', fontSize: 24 }} />
                              </Box>
                            </Tooltip>
                          );
                        }
                                             } else if (eligibilityStatus === false) {
                         if (isGrade10) {
                           // Grade 10: Check if it's a grade issue vs missing courses
                           const programEligibility = programs.find(p => p.id === program.id)?.eligibility;
                           const hasGradeIssues = programEligibility?.gradeIssues && programEligibility.gradeIssues.length > 0;
                           const hasMissingCourses = programEligibility?.missingCourses && programEligibility.missingCourses.length > 0;
                           
                           // Check if this program has early prerequisites (like "Six of 4M, 4U")
                           const hasEarlyPrereqs = programEligibility?.alertMessage?.includes("Next year's courses will be prerequisites");
                           
                           if (hasEarlyPrereqs) {
                             // Grade 10: Yellow circle with white full vertical arrow for early prerequisites
                             return (
                               <Tooltip 
                                 title={
                                   <div>
                                     <div>{programEligibility?.alertMessage || "Next year's courses will be prerequisites for required courses for this program. Be sure to sign up for the appropriate number of 3M or 3U courses to ensure you can be eligible for this program."}</div>
                                     {programEligibility?.nextYearSuggestion && programEligibility.nextYearSuggestion !== "" && (
                                       <div style={{ marginTop: '8px', fontStyle: 'italic', color: '#f44336' }}>
                                         💡 {programEligibility.nextYearSuggestion}
                                       </div>
                                     )}
                                   </div>
                                 }
                                 arrow
                                 componentsProps={{
                                   tooltip: {
                                     sx: {
                                       backgroundColor: '#f5f5f5',
                                       color: '#333',
                                       boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                       fontSize: '14px',
                                       padding: '12px',
                                       maxWidth: '300px'
                                     }
                                   }
                                 }}
                               >
                                 <Box sx={{
                                   width: 24,
                                   height: 24,
                                   borderRadius: '50%',
                                   backgroundColor: '#ffc107', // Yellow
                                   display: 'flex',
                                   alignItems: 'center',
                                   justifyContent: 'center'
                                 }}>
                                   <ArrowUpwardIcon sx={{ color: 'white', fontSize: 16 }} />
                                 </Box>
                               </Tooltip>
                             );
                           } else if (hasGradeIssues) {
                             // Grade 10: Yellow circle with white full vertical arrow for grades need work (prioritize over missing courses)
                             return (
                               <Tooltip 
                                 title={
                                   <div>
                                     <div>{programEligibility?.alertMessage || "You are on the right path but need to improve your grades"}</div>
                                     {programEligibility?.nextYearSuggestion && programEligibility.nextYearSuggestion !== "" && (
                                       <div style={{ marginTop: '8px', fontStyle: 'italic', color: '#f44336' }}>
                                         💡 {programEligibility.nextYearSuggestion}
                                       </div>
                                     )}
                                   </div>
                                 }
                                 arrow
                                 componentsProps={{
                                   tooltip: {
                                     sx: {
                                       backgroundColor: '#f5f5f5',
                                       color: '#333',
                                       boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                       fontSize: '14px',
                                       padding: '12px',
                                       maxWidth: '300px'
                                     }
                                   }
                                 }}
                               >
                                 <Box sx={{
                                   width: 24,
                                   height: 24,
                                   borderRadius: '50%',
                                   backgroundColor: '#ffc107', // Yellow
                                   display: 'flex',
                                   alignItems: 'center',
                                   justifyContent: 'center'
                                 }}>
                                   <ArrowUpwardIcon sx={{ color: 'white', fontSize: 16 }} />
                                 </Box>
                               </Tooltip>
                             );
                           } else if (hasMissingCourses) {
                             // Grade 10: Blue circle with white undo arrow for missing prerequisites (only if no grade issues)
                             return (
                               <Tooltip 
                                 title={
                                   <div>
                                     <div>{programEligibility?.alertMessage || "You need to take additional courses to be eligible"}</div>
                                     {programEligibility?.nextYearSuggestion && programEligibility.nextYearSuggestion !== "" && (
                                       <div style={{ marginTop: '8px', fontStyle: 'italic', color: '#f44336' }}>
                                         💡 {programEligibility.nextYearSuggestion}
                                       </div>
                                     )}
                                   </div>
                                 }
                                 arrow
                                 componentsProps={{
                                   tooltip: {
                                     sx: {
                                       backgroundColor: '#f5f5f5',
                                       color: '#333',
                                       boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                       fontSize: '14px',
                                       padding: '12px',
                                       maxWidth: '300px'
                                     }
                                   }
                                 }}
                               >
                                 <Box sx={{
                                   width: 24,
                                   height: 24,
                                   borderRadius: '50%',
                                   backgroundColor: 'primary.main',
                                   display: 'flex',
                                   alignItems: 'center',
                                   justifyContent: 'center'
                                 }}>
                                   <UndoIcon sx={{ color: 'white', fontSize: 16 }} />
                                 </Box>
                               </Tooltip>
                             );
                           } else {
                             // Grade 10: Fallback - should not happen but just in case
                             return (
                               <Tooltip 
                                 title={
                                   <div>
                                     <div>{programEligibility?.alertMessage || "Not there yet!"}</div>
                                     {programEligibility?.nextYearSuggestion && programEligibility.nextYearSuggestion !== "" && (
                                       <div style={{ marginTop: '8px', fontStyle: 'italic', color: '#f44336' }}>
                                         💡 {programEligibility.nextYearSuggestion}
                                       </div>
                                     )}
                                   </div>
                                 }
                                 arrow
                                 componentsProps={{
                                   tooltip: {
                                     sx: {
                                       backgroundColor: '#f5f5f5',
                                       color: '#333',
                                       boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                       fontSize: '14px',
                                       padding: '12px',
                                       maxWidth: '300px'
                                     }
                                   }
                                 }}
                               >
                                 <Box sx={{
                                   width: 24,
                                   height: 24,
                                   borderRadius: '50%',
                                   backgroundColor: 'primary.main',
                                   display: 'flex',
                                   alignItems: 'center',
                                   justifyContent: 'center'
                                 }}>
                                   <UndoIcon sx={{ color: 'white', fontSize: 16 }} />
                                 </Box>
                               </Tooltip>
                             );
                           }
                         } else {
                           // Other grades: Check if this is a Grade 12 student with low average
                           const programEligibility = programs.find(p => p.id === program.id)?.eligibility;
                           if (userGrade === '12' && 
                               programEligibility?.calculatedAverage && 
                               programEligibility?.programAverage && 
                               programEligibility.calculatedAverage < programEligibility.programAverage &&
                               (!programEligibility.missingCourses || programEligibility.missingCourses.length === 0) &&
                               (!programEligibility.gradeIssues || programEligibility.gradeIssues.length === 0)) {
                             // Grade 12: Blue circle with split arrow for low average (has courses but average too low)
                             return (
                               <Tooltip 
                                 title={
                                   <div>
                                     <div>{programEligibility?.alertMessage || `Your calculated overall average of ${programEligibility.calculatedAverage.toFixed(1)}% does not meet the minimum ${programEligibility.programAverage}% for this program.`}</div>
                                     {programEligibility?.nextYearSuggestion && programEligibility.nextYearSuggestion !== "" && (
                                       <div style={{ marginTop: '8px', fontStyle: 'italic', color: '#f44336' }}>
                                         💡 {programEligibility.nextYearSuggestion}
                                       </div>
                                     )}
                                   </div>
                                 }
                                 arrow
                                 componentsProps={{
                                   tooltip: {
                                     sx: {
                                       backgroundColor: '#f5f5f5',
                                       color: '#333',
                                       boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                       fontSize: '14px',
                                       padding: '12px',
                                       maxWidth: '300px'
                                     }
                                   }
                                 }}
                               >
                                 <Box sx={{
                                   width: 24,
                                   height: 24,
                                   borderRadius: '50%',
                                   backgroundColor: 'primary.main',
                                   display: 'flex',
                                   alignItems: 'center',
                                   justifyContent: 'center'
                                 }}>
                                   <AltRouteIcon sx={{ color: 'white', fontSize: 16 }} />
                                 </Box>
                               </Tooltip>
                             );
                           } else {
                             // Other grades: Original blue circle with split arrow
                             return (
                               <Tooltip 
                                 title={
                                   <div>
                                     <div>{programEligibility?.alertMessage || "Not there yet!"}</div>
                                     {programEligibility?.nextYearSuggestion && programEligibility.nextYearSuggestion !== "" && (
                                       <div style={{ marginTop: '8px', fontStyle: 'italic', color: '#f44336' }}>
                                         💡 {programEligibility.nextYearSuggestion}
                                       </div>
                                     )}
                                   </div>
                                 }
                                 arrow
                                 componentsProps={{
                                   tooltip: {
                                     sx: {
                                       backgroundColor: '#f5f5f5',
                                       color: '#333',
                                       boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                       fontSize: '14px',
                                       padding: '12px',
                                       maxWidth: '300px'
                                     }
                                   }
                                 }}
                               >
                                 <Box sx={{
                                   width: 24,
                                   height: 24,
                                   borderRadius: '50%',
                                   backgroundColor: 'primary.main',
                                   display: 'flex',
                                   alignItems: 'center',
                                   justifyContent: 'center'
                                 }}>
                                   <AltRouteIcon sx={{ color: 'white', fontSize: 16 }} />
                                 </Box>
                               </Tooltip>
                             );
                           }
                         }
                      } else {
                        // isEligible is null - check if there are missing courses or grade issues
                        const programEligibility = programs.find(p => p.id === program.id)?.eligibility;
                        const hasGradeIssues = programEligibility?.gradeIssues && programEligibility.gradeIssues.length > 0;
                        const hasMissingCourses = programEligibility?.missingCourses && programEligibility.missingCourses.length > 0;
                        
                        // Check if this is a Grade 12 student with low average (even when isEligible is null)
                        if (userGrade === '12' && 
                            programEligibility?.calculatedAverage && 
                            programEligibility?.programAverage && 
                            programEligibility.calculatedAverage < programEligibility.programAverage &&
                            (!hasMissingCourses) &&
                            (!hasGradeIssues)) {
                          // Grade 12: Blue circle with split arrow for low average (has courses but average too low)
                          return (
                            <Tooltip 
                              title={
                                <div>
                                  <div>{programEligibility?.alertMessage || `Your calculated overall average of ${programEligibility.calculatedAverage.toFixed(1)}% does not meet the minimum ${programEligibility.programAverage}% for this program.`}</div>
                                  {programEligibility?.nextYearSuggestion && programEligibility.nextYearSuggestion !== "" && (
                                    <div style={{ marginTop: '8px', fontStyle: 'italic', color: '#f44336' }}>
                                      💡 {programEligibility.nextYearSuggestion}
                                    </div>
                                  )}
                                </div>
                              }
                              arrow
                              componentsProps={{
                                tooltip: {
                                  sx: {
                                    backgroundColor: '#f5f5f5',
                                    color: '#333',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                    fontSize: '14px',
                                    padding: '12px',
                                    maxWidth: '300px'
                                  }
                                }
                              }}
                            >
                              <Box sx={{
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                backgroundColor: 'primary.main',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <AltRouteIcon sx={{ color: 'white', fontSize: 16 }} />
                              </Box>
                            </Tooltip>
                          );
                        } else if (hasGradeIssues || hasMissingCourses) {
                          // Show blue split arrow for missing courses or grade issues
                          return (
                            <Tooltip 
                              title={
                                <div>
                                  <div>{programEligibility?.alertMessage || "Not there yet!"}</div>
                                  {programEligibility?.nextYearSuggestion && programEligibility.nextYearSuggestion !== "" && (
                                    <div style={{ marginTop: '8px', fontStyle: 'italic', color: '#f44336' }}>
                                      💡 {programEligibility.nextYearSuggestion}
                                    </div>
                                  )}
                                </div>
                              }
                              arrow
                              componentsProps={{
                                tooltip: {
                                  sx: {
                                    backgroundColor: '#f5f5f5',
                                    color: '#333',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                    fontSize: '14px',
                                    padding: '12px',
                                    maxWidth: '300px'
                                  }
                                }
                              }}
                            >
                              <Box sx={{
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                backgroundColor: 'primary.main',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <AltRouteIcon sx={{ color: 'white', fontSize: 16 }} />
                              </Box>
                            </Tooltip>
                          );
                        } else {
                          // No issues found - show green checklist
                          return (
                            <Tooltip 
                              title={
                                <div>
                                  <div>{programEligibility?.alertMessage || "Prerequisites are not provided for this program"}</div>
                                  {programEligibility?.nextYearSuggestion && programEligibility.nextYearSuggestion !== "" && (
                                    <div style={{ marginTop: '8px', fontStyle: 'italic', color: '#f44336' }}>
                                      💡 {programEligibility.nextYearSuggestion}
                                    </div>
                                  )}
                                </div>
                              }
                              arrow
                              componentsProps={{
                                tooltip: {
                                  sx: {
                                    backgroundColor: '#f5f5f5',
                                    color: '#333',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                    fontSize: '14px',
                                    padding: '12px',
                                    maxWidth: '300px'
                                  }
                                }
                              }}
                            >
                              <Box sx={{
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                backgroundColor: '#8dd439',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <AssignmentIcon sx={{ color: 'white', fontSize: 16 }} />
                              </Box>
                            </Tooltip>
                          );
                        }
                      }
                    })()}
                  </Box>
                </Box>
                <Box component="td" sx={{ p: 1, minWidth: 225, maxWidth: 375, verticalAlign: 'top' }}>
                  <Typography component="span">
                    {program.program}
                  </Typography>
                </Box>
                <Box component="td" sx={{ p: 1, minWidth: 225, maxWidth: 375, verticalAlign: 'top' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {(() => {
                      const school = schools.find(s => s.name === program.school);
                      const schoolImage = school?.feedIcon || school?.resultsImage;
                      
                      // Debug logging for school icons
                      if (program.school === 'Acadia University') {
                        console.log('🎨 School Icon Debug - Program:', program.school, 'School found:', !!school, 'feedIcon:', school?.feedIcon, 'resultsImage:', school?.resultsImage, 'schoolImage:', schoolImage);
                      }
                      
                      // Show loading state if schools are still loading
                      if (schoolsLoading) {
                        return (
                          <Avatar
                            sx={{ 
                              width: 24, 
                              height: 24, 
                              mr: 1, 
                              bgcolor: 'grey.200', 
                              color: 'grey.600', 
                              fontWeight: 600 
                            }}
                          >
                            <CircularProgress size={16} />
                          </Avatar>
                        );
                      }
                      
                      return (
                        <Avatar
                          src={schoolImage}
                          alt={program.school}
                          sx={{ 
                            width: 24, 
                            height: 24, 
                            mr: 1, 
                            bgcolor: 'grey.200', 
                            color: 'grey.600', 
                            fontWeight: 600,
                            border: '0.5px solid black'
                          }}
                        >
                          {!schoolImage && program.school ? program.school[0] : ''}
                        </Avatar>
                      );
                    })()}
                    <Typography component="span">
                      {program.school}
                    </Typography>
                  </Box>
                </Box>
                <Box component="td" sx={{ p: 1, minWidth: 100, maxWidth: 120, verticalAlign: 'top' }}>
                  {program.province}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Custom Eligibility Indicators Modal */}
      <CustomEligibilityIndicatorsModal
        open={eligibilityIndicatorsModalOpen}
        onClose={() => setEligibilityIndicatorsModalOpen(false)}
      />
    </Box>
  );
};

export default ProgramsList; 